import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { publicBookingAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  CalendarDays, Clock, MapPin, ChevronLeft, ChevronRight, CheckCircle2,
  Phone, User, Loader2, ExternalLink,
} from 'lucide-react';

const TYPE_LABELS = {
  consultation: 'Consultation', measurement: 'Measurement', trial: 'Trial Fitting',
  delivery: 'Delivery', alteration_check: 'Alteration Check', other: 'Other',
};

function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [step, setStep] = useState(1); // 1=phone, 2=pick date/time, 3=confirm, 4=done
  const [phone, setPhone] = useState('');
  const [clientName, setClientName] = useState('');
  const [type, setType] = useState('consultation');
  const [notes, setNotes] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [result, setResult] = useState(null);
  const [storeInfo, setStoreInfo] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [settings, setSettings] = useState([]);

  // Load store settings or event info
  useEffect(() => {
    if (slug) {
      publicBookingAPI.eventInfo(slug).then(r => setEventInfo(r.data)).catch(() => toast.error('Event not found'));
    }
    publicBookingAPI.settings().then(r => {
      setStoreInfo(r.data);
      setSettings(r.data?.settings || []);
    }).catch(() => {});
  }, [slug]);

  // Load slots when date changes
  useEffect(() => {
    if (!selectedDate) return;
    setSlotsLoading(true);
    const params = eventInfo ? { popup_event_id: eventInfo.id } : {};
    publicBookingAPI.slots(selectedDate, params)
      .then(r => { setSlots(r.data || []); setSlotsLoading(false); })
      .catch(() => setSlotsLoading(false));
  }, [selectedDate, eventInfo]);

  // Calendar days for the month view
  const monthDays = useMemo(() => {
    const y = currentMonth.getFullYear(), m = currentMonth.getMonth();
    const first = new Date(y, m, 1);
    const start = new Date(first);
    start.setDate(start.getDate() - first.getDay());
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [currentMonth]);

  const today = formatDate(new Date());

  // Check if a day is open based on settings
  const isDayOpen = (d) => {
    if (eventInfo) {
      const ds = formatDate(d);
      return ds >= eventInfo.start_date && ds <= eventInfo.end_date;
    }
    const dayOfWeekSun = d.getDay(); // Sun=0
    const setting = settings.find(s => s.day_of_week === dayOfWeekSun);
    return setting ? setting.is_open : false;
  };

  const handleBook = async () => {
    if (!phone || phone.length < 10) { toast.error('Enter a valid phone number'); return; }
    if (!selectedDate || !selectedSlot) { toast.error('Pick a date and time'); return; }
    setBooking(true);
    try {
      const res = await publicBookingAPI.book({
        phone, client_name: clientName || undefined,
        appointment_type: type, date: selectedDate,
        start_time: selectedSlot, notes: notes || undefined,
        popup_event_id: eventInfo?.id || undefined,
      });
      setResult(res.data);
      setStep(4);
      toast.success('Appointment booked!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed');
    }
    setBooking(false);
  };

  const storeName = eventInfo?.name || storeInfo?.store_name || 'Herringbone & Sui';
  const storeAddress = eventInfo ? `${eventInfo.venue_name}, ${eventInfo.venue_address}` : (storeInfo?.store_address || 'Colaba, Mumbai');

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3"
            style={{ backgroundColor: '#1B2A4A' }}>
            <span className="text-lg font-bold" style={{ color: '#C5A55A', fontFamily: '"Bodoni Moda", Georgia, serif' }}>H</span>
          </div>
          <h1 className="text-2xl font-semibold" style={{ color: '#1B2A4A', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
            {storeName}
          </h1>
          <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
            {eventInfo ? `${eventInfo.start_date} — ${eventInfo.end_date}` : 'Book your appointment'}
          </p>
          {storeAddress && <p className="text-xs mt-0.5 flex items-center justify-center gap-1" style={{ color: '#9CA3AF' }}>
            <MapPin className="w-3 h-3" /> {storeAddress}
          </p>}
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s ? 'text-white' : ''}`}
                style={{ backgroundColor: step >= s ? '#1B2A4A' : 'rgba(27,42,74,0.08)', color: step >= s ? '#fff' : '#9CA3AF' }}>
                {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className="w-8 h-0.5" style={{ backgroundColor: step > s ? '#1B2A4A' : 'rgba(27,42,74,0.08)' }} />}
            </div>
          ))}
        </div>

        {/* Step 1: Phone */}
        {step === 1 && (
          <Card className="rounded-xl shadow-sm border-0" data-testid="booking-step-phone">
            <CardHeader className="pb-2">
              <CardTitle className="text-base" style={{ color: '#1B2A4A' }}>Your Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs" style={{ color: '#6B7280' }}>Phone Number *</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-2.5 top-2 w-4 h-4" style={{ color: '#9CA3AF' }} />
                  <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98765 43210"
                    className="pl-9 h-9 text-sm" data-testid="phone-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs" style={{ color: '#6B7280' }}>Name (optional)</Label>
                <div className="relative mt-1">
                  <User className="absolute left-2.5 top-2 w-4 h-4" style={{ color: '#9CA3AF' }} />
                  <Input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Your name"
                    className="pl-9 h-9 text-sm" data-testid="name-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs" style={{ color: '#6B7280' }}>Appointment Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="h-9 mt-1 text-sm" data-testid="booking-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full h-10 mt-2 text-sm font-medium" style={{ backgroundColor: '#1B2A4A' }}
                onClick={() => { if (!phone || phone.length < 10) { toast.error('Enter a valid phone number'); return; } setStep(2); }}
                data-testid="continue-to-date-btn">
                Continue
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Pick Date & Time */}
        {step === 2 && (
          <Card className="rounded-xl shadow-sm border-0" data-testid="booking-step-date">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base" style={{ color: '#1B2A4A' }}>Pick Date & Time</CardTitle>
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => setStep(1)}>
                  <ChevronLeft className="w-3 h-3 mr-1" /> Back
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mini calendar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <button onClick={() => { const d = new Date(currentMonth); d.setMonth(d.getMonth() - 1); setCurrentMonth(d); }}
                    className="p-1 rounded hover:bg-gray-100"><ChevronLeft className="w-4 h-4" /></button>
                  <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
                    {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </p>
                  <button onClick={() => { const d = new Date(currentMonth); d.setMonth(d.getMonth() + 1); setCurrentMonth(d); }}
                    className="p-1 rounded hover:bg-gray-100"><ChevronRight className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {DAYS.map(d => <div key={d} className="text-center text-[10px] font-semibold py-1" style={{ color: '#9CA3AF' }}>{d}</div>)}
                  {monthDays.map((day, i) => {
                    const ds = formatDate(day);
                    const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                    const isToday = ds === today;
                    const isPast = ds < today;
                    const isOpen = isDayOpen(day);
                    const isSelected = ds === selectedDate;
                    const disabled = isPast || !isCurrentMonth || !isOpen;
                    return (
                      <button key={i} disabled={disabled}
                        className={`text-[11px] h-8 rounded-lg font-medium transition-colors ${disabled ? 'opacity-25 cursor-not-allowed' : 'hover:bg-blue-50 cursor-pointer'}`}
                        style={{
                          backgroundColor: isSelected ? '#1B2A4A' : isToday ? 'rgba(27,42,74,0.06)' : 'transparent',
                          color: isSelected ? '#fff' : isToday ? '#1B2A4A' : '#374151',
                        }}
                        onClick={() => { setSelectedDate(ds); setSelectedSlot(''); }}
                        data-testid={`pub-cal-${ds}`}>
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time slots */}
              {selectedDate && (
                <div>
                  <Label className="text-xs mb-1.5 block" style={{ color: '#6B7280' }}>
                    <Clock className="w-3 h-3 inline mr-1" />Available Times — {selectedDate}
                  </Label>
                  {slotsLoading ? <Skeleton className="h-16" /> : (
                    <div className="grid grid-cols-3 gap-1.5">
                      {slots.filter(s => s.available > 0).map(s => (
                        <button key={s.start_time} onClick={() => setSelectedSlot(s.start_time)}
                          className={`px-2 py-2 text-xs rounded-lg border transition-colors font-medium ${selectedSlot === s.start_time ? 'text-white border-transparent' : 'hover:bg-gray-50'}`}
                          style={selectedSlot === s.start_time ? { backgroundColor: '#1B2A4A' } : { borderColor: 'rgba(27,42,74,0.12)', color: '#374151' }}
                          data-testid={`pub-slot-${s.start_time}`}>
                          {s.start_time}
                        </button>
                      ))}
                      {slots.filter(s => s.available > 0).length === 0 && (
                        <p className="col-span-3 text-xs text-center py-4" style={{ color: '#9CA3AF' }}>No available slots for this day</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <Button className="w-full h-10 text-sm font-medium" style={{ backgroundColor: '#1B2A4A' }}
                disabled={!selectedDate || !selectedSlot}
                onClick={() => setStep(3)}
                data-testid="continue-to-confirm-btn">
                Continue
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <Card className="rounded-xl shadow-sm border-0" data-testid="booking-step-confirm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base" style={{ color: '#1B2A4A' }}>Confirm Booking</CardTitle>
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => setStep(2)}>
                  <ChevronLeft className="w-3 h-3 mr-1" /> Back
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 rounded-lg space-y-2" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" style={{ color: '#1B2A4A' }} />
                  <span className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{selectedDate}</span>
                  <span className="text-sm" style={{ color: '#6B7280' }}>at {selectedSlot}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" style={{ color: '#6B7280' }} />
                  <span className="text-sm" style={{ color: '#374151' }}>{phone}</span>
                </div>
                {clientName && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" style={{ color: '#6B7280' }} />
                    <span className="text-sm" style={{ color: '#374151' }}>{clientName}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" style={{ color: '#6B7280' }} />
                  <span className="text-sm" style={{ color: '#374151' }}>{TYPE_LABELS[type]}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" style={{ color: '#6B7280' }} />
                  <span className="text-sm" style={{ color: '#374151' }}>{storeAddress}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs" style={{ color: '#6B7280' }}>Notes (optional)</Label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 text-xs h-16" placeholder="Anything we should know..." />
              </div>
              <Button className="w-full h-10 text-sm font-medium gap-1.5" style={{ backgroundColor: '#1B2A4A' }}
                onClick={handleBook} disabled={booking} data-testid="confirm-booking-btn">
                {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {booking ? 'Booking...' : 'Confirm Appointment'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Done */}
        {step === 4 && result && (
          <Card className="rounded-xl shadow-sm border-0" data-testid="booking-step-done">
            <CardContent className="py-8 text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
                <CheckCircle2 className="w-8 h-8" style={{ color: '#059669' }} />
              </div>
              <div>
                <h2 className="text-xl font-semibold" style={{ color: '#1B2A4A' }}>You're Booked!</h2>
                <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
                  {result.date} at {result.start_time} — {result.end_time}
                </p>
                <p className="text-xs mt-0.5" style={{ color: '#9CA3AF' }}>{TYPE_LABELS[result.appointment_type]}</p>
              </div>
              {result.gcal_url && (
                <a href={result.gcal_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border hover:bg-gray-50 transition-colors"
                  style={{ borderColor: 'rgba(27,42,74,0.12)', color: '#1B2A4A' }} data-testid="pub-gcal-link">
                  <ExternalLink className="w-3.5 h-3.5" /> Add to Google Calendar
                </a>
              )}
              <p className="text-xs" style={{ color: '#9CA3AF' }}>
                You'll receive a WhatsApp reminder before your appointment.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-[10px] mt-6" style={{ color: '#D1D5DB' }}>
          Powered by Herringbone & Sui
        </p>
      </div>
    </div>
  );
}
